import { useMemo } from "react";
import { T } from "./exports";

function getBgStyle(bg, bgColor) {
  if (bg === "black")      return { backgroundColor: "#000000" };
  if (bg === "white")      return { backgroundColor: "#ffffff" };
  if (bg === "custom")     return { backgroundColor: bgColor || "#1a2228" };
  if (bg === "grid")       return { backgroundColor: "#1a2228", backgroundImage: "linear-gradient(rgba(100,160,180,0.12) 1px,transparent 1px),linear-gradient(90deg,rgba(100,160,180,0.12) 1px,transparent 1px)", backgroundSize: "24px 24px" };
  if (bg === "grid-light") return { backgroundColor: "#f0f4f5", backgroundImage: "linear-gradient(rgba(0,0,0,0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.1) 1px,transparent 1px)", backgroundSize: "24px 24px" };
  return { backgroundColor: "#1a2228" }; // dark (default)
}

function getIframeBg(bg, bgColor) {
  if (bg === "black")      return { hex: "#000000", extra: "" };
  if (bg === "white")      return { hex: "#ffffff", extra: "" };
  if (bg === "custom")     return { hex: bgColor || "#1a2228", extra: "" };
  if (bg === "grid")       return { hex: "#1a2228", extra: "background-image:linear-gradient(rgba(100,160,180,.12) 1px,transparent 1px),linear-gradient(90deg,rgba(100,160,180,.12) 1px,transparent 1px);background-size:24px 24px" };
  if (bg === "grid-light") return { hex: "#f0f4f5", extra: "background-image:linear-gradient(rgba(0,0,0,.1) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.1) 1px,transparent 1px);background-size:24px 24px" };
  return { hex: "#1a2228", extra: "" };
}

function buildHtml(url, bg, bgColor, autoRotate, cameraControls, exposure, shadowIntensity) {
  const { hex, extra } = getIframeBg(bg, bgColor);
  const safeUrl = String(url).replace(/"/g, "%22");
  const isLight = bg === "white" || bg === "grid-light";
  const spinColor = isLight ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.25)";
  const spinAccent = isLight ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.8)";
  const textColor = isLight ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)";
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:100%;height:100%;overflow:hidden;background:${hex};${extra ? extra + ";" : ""}}
model-viewer{width:100%;height:100%;--progress-bar-color:transparent}
#loader{position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;pointer-events:none;transition:opacity 0.35s ease;z-index:9}
#loader.hidden{opacity:0}
@keyframes spin{to{transform:rotate(360deg)}}
.ring{width:30px;height:30px;border:3px solid ${spinColor};border-top-color:${spinAccent};border-radius:50%;animation:spin 0.75s linear infinite}
#msg{font:11px/1 system-ui,sans-serif;color:${textColor};letter-spacing:.02em}
</style>
<script type="module" src="https://unpkg.com/@google/model-viewer@3.5.0/dist/model-viewer.min.js"></script>
</head>
<body>
<model-viewer id="mv" src="${safeUrl}"${autoRotate ? " auto-rotate" : ""}${cameraControls ? " camera-controls" : ""} exposure="${exposure || 1}" shadow-intensity="${shadowIntensity || 1}"></model-viewer>
<div id="loader"><div class="ring"></div><div id="msg">Loading…</div></div>
<script>
var mv=document.getElementById('mv');
var loader=document.getElementById('loader');
var msg=document.getElementById('msg');
mv.addEventListener('progress',function(e){var p=Math.round((e.detail.totalProgress||0)*100);msg.textContent=p>0?p+'%':'Loading…';});
mv.addEventListener('load',function(){loader.classList.add('hidden');setTimeout(function(){loader.style.display='none';},400);});
mv.addEventListener('error',function(){msg.textContent='Failed to load';loader.querySelector('.ring').style.display='none';});
</script>
</body>
</html>`;
}

export function Model3DViewer({ url, bg, bgColor, autoRotate, cameraControls, exposure, shadowIntensity, borderRadius }) {
  const html = useMemo(
    () => url ? buildHtml(url, bg, bgColor, autoRotate, cameraControls, exposure, shadowIntensity) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, bg, bgColor, autoRotate, cameraControls, exposure, shadowIntensity]
  );

  if (!html) {
    return (
      <div style={{ width: "100%", height: "100%", borderRadius, overflow: "hidden", position: "relative", ...getBgStyle(bg, bgColor) }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textSub, gap: 6, pointerEvents: "none" }}>
          <span style={{ fontSize: 28, opacity: 0.5 }}>⬢</span>
          <span style={{ fontSize: 10 }}>3D Model</span>
          <span style={{ fontSize: 8, color: T.textMute }}>Paste GLB / GLTF URL in Content</span>
        </div>
      </div>
    );
  }

  return (
    <iframe
      srcDoc={html}
      title="3D Model Viewer"
      sandbox="allow-scripts"
      style={{ width: "100%", height: "100%", border: "none", borderRadius, display: "block" }}
    />
  );
}
